import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  SmallBody,
  TimeAgoRenderer,
  Card,
  Body,
  Tooltip,
  PdfModal,
  Loader,
  ConfirmationModal,
  Button as BaseButton
} from '@ubeya/shared-web/components';
import { ReactComponent as BaseDeleteIcon } from '@ubeya/shared-web/assets/delete.svg';
import { ReactComponent as BaseFileIcon } from '@ubeya/shared-web/assets/file.svg';
import { ReactComponent as BaseDownloadIcon } from '@ubeya/shared-web/assets/compliance-download.svg';
import { ReactComponent as BaseResendIcon } from '@ubeya/shared-web/assets/arrows-circle.svg';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { LEGENDS_US_BARCLAYS_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';

const SendEmailLoader = styled(Loader)`
  width: 12px;
  height: 12px;
`;

const SendEmailButton = styled(BaseButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
  min-width: 96px;
  font-size: 11px;
  padding: 0 8px;
  white-space: nowrap;
  font-weight: 500;
`;

const ResendIcon = styled(BaseResendIcon)`
  width: 11px;
  height: 11px;
  flex-shrink: 0;

  path {
    stroke: currentColor;
  }
`;

const Container = styled(Card)`
  margin-bottom: 10px;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  padding: 12px 14px;
  gap: 12px;
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CommentText = styled(Body)`
  word-wrap: break-word;
  font-size: 13px;
  padding: 0;
`;

const AttachmentChip = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 20px;
  font-size: 12px;
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ChipViewArea = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 5px 10px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const ChipDownloadArea = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px 5px 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};

  &:hover path {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;

const AttachmentChipName = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileIcon = styled(BaseFileIcon)`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const ChipDownloadIcon = styled(BaseDownloadIcon)`
  width: 13px;
  height: 13px;
  flex-shrink: 0;
`;

const RightSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
`;

const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  gap: 2px;
`;

const UserName = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const TimeAgo = styled(SmallBody)`
  color: ${({ theme }) => theme.colors.disabled};
  font-size: 11px;
`;

const CommentType = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme, severity }) =>
    severity === 2 ? theme.colors.redNegative : severity === 3 ? theme.colors.yellowPartially : 'inherit'};
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const EmailButtonSlot = styled.div`
  min-width: 96px;
`;

const DeleteIcon = styled(BaseDeleteIcon)`
  height: 15px;
  width: 15px;
  cursor: pointer;

  path {
    fill: ${({ theme }) => theme.colors.grey4};
  }

  &:hover path {
    fill: ${({ theme }) => theme.colors.redNegative};
  }
`;

const Comment = ({
  id,
  type,
  comment,
  firstName,
  t,
  lastName,
  createdAt,
  file,
  emailSentAt,
  employeeEmail,
  accountId,
  deleteComment,
  sendCommentEmail,
  showDelete = true
}) => {
  const formattingMap = useFormattingMap();
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSendEmailConfirm, setShowSendEmailConfirm] = useState(false);

  const isBarclaysAccount = accountId === LEGENDS_US_BARCLAYS_ACCOUNT_ID;

  const onDeleteComment = useCallback(() => deleteComment({ id }), [deleteComment, id]);
  const onSendEmail = useCallback(async () => {
    setShowSendEmailConfirm(false);
    setIsSendingEmail(true);
    try {
      await sendCommentEmail({ commentId: id });
    } finally {
      setIsSendingEmail(false);
    }
  }, [sendCommentEmail, id]);

  const emailSentAtFormatted = emailSentAt ? formattingMap.parsedLocalTime(emailSentAt) : null;

  const attachmentFileName = file
    ? file.name || (file.mimeType ? `${t('attachment')}.${file.mimeType.split('/')[1]}` : t('attachment'))
    : null;

  const onChipClick = useCallback(
    (e) => {
      const isPdf = file?.mimeType === 'application/pdf';
      if (isPdf && file?.url) {
        e.preventDefault();
        setPdfPreviewUrl(file.url);
      }
    },
    [file]
  );

  return (
    <Container className="employee-comments">
      <ContentWrapper>
        <CommentText>{comment}</CommentText>
        {file?.url && (
          <AttachmentChip>
            <Tooltip body={t('view')}>
              <ChipViewArea href={file.url} target="_blank" rel="noopener noreferrer" onClick={onChipClick}>
                <FileIcon />
                <AttachmentChipName title={attachmentFileName}>{attachmentFileName}</AttachmentChipName>
              </ChipViewArea>
            </Tooltip>
            <Tooltip body={t('download')}>
              <ChipDownloadArea href={file.url} target="_blank" rel="noopener noreferrer">
                <ChipDownloadIcon />
              </ChipDownloadArea>
            </Tooltip>
          </AttachmentChip>
        )}
      </ContentWrapper>

      <RightSection>
        <MetaColumn>
          <UserName>{[firstName, lastName].filter(Boolean).join(' ')}</UserName>
          <TimeAgo>
            <TimeAgoRenderer rowData={{ createdAt }} dataKey="createdAt" disabledTooltip />
          </TimeAgo>
          {type && type.slug !== 'other' && <CommentType severity={type?.severity}>{t(type.slug)}</CommentType>}
        </MetaColumn>

        <ActionsRow>
          {showDelete && (
            <Tooltip body={t('delete')}>
              <DeleteIcon onClick={() => setShowDeleteConfirm(true)} />
            </Tooltip>
          )}

          {isBarclaysAccount && file?.url && type?.slug === 'comment__eca' && (
            <EmailButtonSlot>
              {isSendingEmail ? (
                <SendEmailButton type="button" secondary narrow disabled>
                  <SendEmailLoader size="xsmall" thickness="xsmall" />
                </SendEmailButton>
              ) : emailSentAt ? (
                <Tooltip body={`${t('sentOn')} ${emailSentAtFormatted}`}>
                  <SendEmailButton type="button" secondary narrow onClick={() => setShowSendEmailConfirm(true)}>
                    {t('resendEmail')}
                    <ResendIcon />
                  </SendEmailButton>
                </Tooltip>
              ) : (
                <Tooltip body={!employeeEmail ? t('noEmailToSendAttachment') : t('sendAttachmentByEmail')}>
                  <SendEmailButton
                    type="button"
                    secondary
                    narrow
                    disabled={!employeeEmail}
                    onClick={employeeEmail ? () => setShowSendEmailConfirm(true) : undefined}>
                    {t('sendEmail')}
                  </SendEmailButton>
                </Tooltip>
              )}
            </EmailButtonSlot>
          )}
        </ActionsRow>
      </RightSection>
      {pdfPreviewUrl && <PdfModal url={pdfPreviewUrl} onClose={() => setPdfPreviewUrl(null)} />}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={t('deleteComment')}
          body={t('areYouSureDeleteComment')}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            onDeleteComment();
          }}
          confirmText={t('delete')}
          isPermanentAction
        />
      )}
      {showSendEmailConfirm && (
        <ConfirmationModal
          title={t('sendAttachmentByEmail')}
          body={t('sendCommentAttachmentEmailConfirm')}
          onClose={() => setShowSendEmailConfirm(false)}
          onConfirm={onSendEmail}
          confirmText={t('send')}
        />
      )}
    </Container>
  );
};

export default Comment;
